/**
 * Memory Categories Service
 * 
 * This service handles all API calls related to memory categories, milestones, and analytics.
 */

const API_BASE = process.env.REACT_APP_BACKEND_URL + '/api';

/**
 * Get authentication headers
 */
const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json'
  };
};

/**
 * Get category statistics for current user
 * @returns {Promise<Object>} Category stats including domains, tags, severity distribution
 */
export const getCategoryStats = async () => {
  const response = await fetch(`${API_BASE}/chat/categories/stats`, {
    headers: getAuthHeaders()
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to get category stats');
  }

  return await response.json();
};

/**
 * Get messages by category/domain
 * @param {string} domain - ASQ domain (communications, gross_motor, fine_motor, cognitive, personal_social, general)
 * @param {Object} filters - Filter options
 * @param {number} filters.limit - Number of messages to return (default: 20)
 * @param {number} filters.offset - Pagination offset (default: 0)
 * @param {number} filters.min_importance - Minimum importance score (0.0-1.0)
 * @param {string} filters.severity - Severity filter (high, medium, low, none)
 * @returns {Promise<Object>} Messages and total count
 */
export const getMessagesByCategory = async (domain, filters = {}) => {
  const params = new URLSearchParams();
  if (filters.limit) params.append('limit', filters.limit);
  if (filters.offset) params.append('offset', filters.offset);
  if (filters.min_importance !== undefined) params.append('min_importance', filters.min_importance);
  if (filters.severity) params.append('severity', filters.severity);

  const queryString = params.toString();
  const url = `${API_BASE}/chat/categories/${domain}${queryString ? '?' + queryString : ''}`;

  const response = await fetch(url, {
    headers: getAuthHeaders()
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to get messages by category');
  }

  return await response.json();
};

/**
 * Get milestone details
 * @param {number} milestoneId - ASQ milestone ID (1-196)
 * @returns {Promise<Object>} Milestone details including usage stats
 */
export const getMilestoneDetails = async (milestoneId) => {
  const response = await fetch(`${API_BASE}/chat/categories/milestones/${milestoneId}`, {
    headers: getAuthHeaders()
  });

  if (!response.ok) {
    if (response.status === 404) {
      return null;
    }
    const error = await response.json();
    throw new Error(error.detail || 'Failed to get milestone details');
  }

  return await response.json();
};

/**
 * Search milestones by text query
 * @param {string} query - Text query for semantic search
 * @param {Object} filters - Filter options
 * @param {string} filters.domain - Filter by ASQ domain
 * @param {number} filters.age_months - Filter by child age in months
 * @param {number} filters.limit - Number of results (default: 10, max: 50)
 * @returns {Promise<Object>} Search results with matched milestones
 */
export const searchMilestones = async (query, filters = {}) => {
  const params = new URLSearchParams();
  params.append('query', query);
  if (filters.domain) params.append('domain', filters.domain);
  if (filters.age_months) params.append('age_months', filters.age_months);
  if (filters.limit) params.append('limit', filters.limit);

  const response = await fetch(`${API_BASE}/chat/categories/milestones/search?${params.toString()}`, {
    headers: getAuthHeaders()
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to search milestones');
  }

  return await response.json();
};

/**
 * Get important memories (high importance score)
 * @param {Object} filters - Filter options
 * @param {number} filters.limit - Number of memories (default: 20, max: 100)
 * @param {string} filters.domain - Filter by ASQ domain
 * @param {number} filters.min_score - Minimum importance score (default: 0.7)
 * @param {string} filters.severity - Filter by severity level
 * @returns {Promise<Object>} Important memories list
 */
export const getImportantMemories = async (filters = {}) => {
  const params = new URLSearchParams();
  if (filters.limit) params.append('limit', filters.limit);
  if (filters.domain) params.append('domain', filters.domain);
  if (filters.min_score !== undefined) params.append('min_score', filters.min_score);
  if (filters.severity) params.append('severity', filters.severity);

  const response = await fetch(`${API_BASE}/chat/categories/memories/important?${params.toString()}`, {
    headers: getAuthHeaders()
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to get important memories');
  }

  return await response.json();
};

/**
 * Get archived memories
 * @param {Object} pagination - Pagination options
 * @param {number} pagination.limit - Number of messages (default: 20, max: 100)
 * @param {number} pagination.offset - Pagination offset (default: 0)
 * @returns {Promise<Object>} Archived messages list
 */
export const getArchivedMemories = async (pagination = {}) => {
  const params = new URLSearchParams();
  if (pagination.limit) params.append('limit', pagination.limit);
  if (pagination.offset) params.append('offset', pagination.offset);

  const response = await fetch(`${API_BASE}/chat/categories/memories/archived?${params.toString()}`, {
    headers: getAuthHeaders()
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to get archived memories');
  }

  return await response.json();
};

/**
 * Restore an archived message
 * @param {string} messageId - Message ID to restore
 * @returns {Promise<Object>} Restore confirmation
 */
export const restoreArchivedMessage = async (messageId) => {
  const response = await fetch(`${API_BASE}/chat/categories/memories/${messageId}/restore`, {
    method: 'POST',
    headers: getAuthHeaders()
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to restore message');
  }

  return await response.json();
};

/**
 * Reclassify a message (admin only)
 * @param {string} messageId - Message ID to reclassify
 * @param {Object} data - Reclassification data
 * @param {string} data.new_domain - New ASQ domain
 * @param {Array<string>} data.new_tags - New secondary tags
 * @param {string} data.severity - New severity level
 * @param {string} data.reason - Reason for reclassification
 * @returns {Promise<Object>} Reclassification confirmation
 */
export const reclassifyMessage = async (messageId, data) => {
  const response = await fetch(`${API_BASE}/chat/categories/reclassify/${messageId}`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({
      new_domain: data.new_domain,
      new_tags: data.new_tags,
      severity: data.severity,
      reason: data.reason
    })
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to reclassify message');
  }

  return await response.json();
};

/**
 * Get memory analytics (admin only)
 * @param {string} period - Time period: "7d", "30d", or "90d"
 * @returns {Promise<Object>} Comprehensive memory analytics
 */
export const getMemoryAnalytics = async (period = '7d') => {
  const response = await fetch(`${API_BASE}/admin/memory/analytics?period=${period}`, {
    headers: getAuthHeaders()
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to get memory analytics');
  }

  return await response.json();
};

